import React, { useState, useEffect, useCallback } from 'react';
import { wissenApi } from '../api';

const card = { background: '#1e293b', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid #334155' };
const btn = (color = '#6366f1') => ({
  background: color, color: '#fff', border: 'none', borderRadius: 6,
  padding: '6px 14px', cursor: 'pointer', fontSize: 13,
});

const TYPEN_LABEL = {
  klausel: '📋 Klausel', feld: '🔤 Feld', zusammenfassung: '📄 Zusammenfassung',
  risiko: '⚠️ Risiko', preis: '💰 Preis', frist: '⏰ Frist', anbieter: '🏢 Anbieter',
  leistung: '✅ Leistung', bedingung: '📑 Bedingung', allgemein: '🗂️ Allgemein',
};

export default function WissensbasisPage() {
  const [tab, setTab] = useState('wissen');
  const [wissen, setWissen] = useState([]);
  const [anfragen, setAnfragen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [frage, setFrage] = useState('');
  const [frageLoading, setFrageLoading] = useState(false);
  const [letzteAntwort, setLetzteAntwort] = useState(null);
  const [suchQuery, setSuchQuery] = useState('');
  const [suchErgebnisse, setSuchErgebnisse] = useState(null);
  const [filter, setFilter] = useState({ typ: '', verifiziert: '' });

  const loadWissen = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.typ) params.typ = filter.typ;
      if (filter.verifiziert !== '') params.verifiziert = filter.verifiziert;
      const r = await wissenApi.list(params);
      setWissen(Array.isArray(r.data) ? r.data : []);
    } catch {}
    setLoading(false);
  }, [filter]);

  const loadAnfragen = useCallback(async () => {
    try {
      const r = await wissenApi.fragenliste({ limit: 20 });
      setAnfragen(Array.isArray(r.data) ? r.data : []);
    } catch {}
  }, []);

  useEffect(() => { loadWissen(); }, [loadWissen]);
  useEffect(() => { if (tab === 'anfragen') loadAnfragen(); }, [tab, loadAnfragen]);

  async function handleFrage() {
    if (!frage.trim()) return;
    setFrageLoading(true);
    setLetzteAntwort(null);
    try {
      const r = await wissenApi.fragen(frage.trim());
      setLetzteAntwort(r.data);
      setAnfragen(prev => [r.data, ...prev]);
    } catch (e) {
      setLetzteAntwort({ frage: frage.trim(), antwort: 'Fehler: ' + (e.response?.data?.error || e.message), status: 'fehlgeschlagen' });
    }
    setFrageLoading(false);
  }

  async function handleSuche() {
    if (!suchQuery.trim()) return;
    try {
      const [wissensR, klauselR] = await Promise.all([
        wissenApi.suche(suchQuery),
        wissenApi.klauselSuche(suchQuery),
      ]);
      setSuchErgebnisse({ wissen: wissensR.data || [], klauseln: klauselR.data || [] });
    } catch {}
  }

  async function handleVerifizieren(id) {
    try {
      await wissenApi.verifizieren(id);
      loadWissen();
    } catch {}
  }

  async function handleDelete(id) {
    if (!window.confirm('Wissenseintrag archivieren?')) return;
    try {
      await wissenApi.delete(id);
      loadWissen();
    } catch {}
  }

  const tabStyle = (t) => ({
    padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 700 : 400,
    background: tab === t ? '#818cf8' : '#334155', color: tab === t ? '#fff' : '#94a3b8', border: 'none',
  });

  const RISIKO_COLOR = { hoch: '#ef4444', mittel: '#f59e0b', niedrig: '#22c55e', hoch: '#ef4444' };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto', color: '#f1f5f9' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>🧠 Vertragswissensbasis</h1>
      <p style={{ color: '#64748b', marginBottom: 20, fontSize: 14 }}>
        Automatisch indexiertes Vertragswissen aus Dokumenten, Klauseln und Feldern.
      </p>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['wissen', '🗂️ Wissensbasis'], ['fragen', '💬 Fragen & Antworten'], ['anfragen', '📋 Anfragen-Historie'], ['suche', '🔎 Wissensuche']].map(([t, l]) => (
          <button key={t} style={tabStyle(t)} onClick={() => setTab(t)}>{l}</button>
        ))}
      </div>

      {/* Tab: Wissensbasis */}
      {tab === 'wissen' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <select value={filter.typ} onChange={e => setFilter(f => ({ ...f, typ: e.target.value }))}
              style={{ background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155', borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
              <option value="">Alle Typen</option>
              {Object.entries(TYPEN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select value={filter.verifiziert} onChange={e => setFilter(f => ({ ...f, verifiziert: e.target.value }))}
              style={{ background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155', borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
              <option value="">Alle</option>
              <option value="true">✅ Verifiziert</option>
              <option value="false">⏳ Unverifiziert</option>
            </select>
          </div>

          {loading ? (
            <div style={{ color: '#6366f1', textAlign: 'center', padding: 40 }}>Laden...</div>
          ) : wissen.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', color: '#64748b' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🧠</div>
              <div>Noch kein Wissen indexiert.</div>
              <div style={{ fontSize: 12, marginTop: 8 }}>Lade Dokumente hoch und starte die Verarbeitung (v2), um die Wissensbasis zu befüllen.</div>
            </div>
          ) : (
            <div>
              <div style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>{wissen.length} Einträge</div>
              {wissen.map(w => (
                <div key={w.id} style={{ ...card, borderLeft: `3px solid ${w.verifiziert ? '#22c55e' : '#6366f1'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 12, background: '#334155', padding: '2px 8px', borderRadius: 10 }}>
                          {TYPEN_LABEL[w.wissenstyp] || w.wissenstyp}
                        </span>
                        {w.verifiziert ? <span style={{ color: '#22c55e', fontSize: 12 }}>✅ Verifiziert</span> : null}
                        {w.konfidenz ? <span style={{ color: '#64748b', fontSize: 11 }}>Konfidenz: {Math.round(w.konfidenz * 100)}%</span> : null}
                      </div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>{w.titel || w.wissenstyp}</div>
                      <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.5 }}>{w.inhalt?.slice(0, 300)}{w.inhalt?.length > 300 ? '…' : ''}</div>
                      {w.originaldateiname && (
                        <div style={{ color: '#64748b', fontSize: 11, marginTop: 6 }}>📄 {w.originaldateiname}</div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      {!w.verifiziert && (
                        <button onClick={() => handleVerifizieren(w.id)} className="btn-primary">✅ Verifizieren</button>
                      )}
                      <button onClick={() => handleDelete(w.id)} className="btn-danger">🗑</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Tab: Fragen & Antworten */}
      {tab === 'fragen' && (
        <div>
          <div style={card}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: 16 }}>Frage stellen</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={frage}
                onChange={e => setFrage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleFrage()}
                placeholder="Frage an die Wissensbasis… z.B. 'Welche Kündigungsfristen gelten?'"
                style={{
                  flex: 1, background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                  color: '#f1f5f9', padding: '10px 14px', fontSize: 14,
                }}
              />
              <button onClick={handleFrage} disabled={frageLoading || !frage.trim()} style={btn(frageLoading ? '#334155' : '#6366f1')}>
                {frageLoading ? '⏳ Suche…' : '🔎 Fragen'}
              </button>
            </div>
            <div style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>
              Beantwortet auf Basis indexierter Vertragsdokumente, Klauseln und Felder. Keine KI-Verbindung erforderlich.
            </div>
          </div>

          {letzteAntwort && (
            <div style={{ ...card, borderLeft: '3px solid #6366f1' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>❓ {letzteAntwort.frage}</div>
              <div style={{ color: '#94a3b8', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontSize: 13 }}>
                {letzteAntwort.antwort}
              </div>
              {letzteAntwort.quellen_json && (() => {
                try {
                  const q = JSON.parse(letzteAntwort.quellen_json);
                  if (q.length > 0) return (
                    <div style={{ marginTop: 12, borderTop: '1px solid #334155', paddingTop: 10 }}>
                      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 6 }}>Quellen: {q.length}</div>
                      {q.map((src, i) => (
                        <div key={i} style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>
                          [{i + 1}] {src.titel} ({src.wissenstyp}) – {src.dokument || src.anbieter || ''}
                        </div>
                      ))}
                    </div>
                  );
                } catch {}
              })()}
            </div>
          )}
        </div>
      )}

      {/* Tab: Anfragen-Historie */}
      {tab === 'anfragen' && (
        <div>
          {anfragen.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', color: '#64748b' }}>Noch keine Anfragen gestellt.</div>
          ) : anfragen.map(a => (
            <div key={a.id} style={{ ...card, borderLeft: `3px solid ${a.status === 'beantwortet' ? '#22c55e' : '#ef4444'}` }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>❓ {a.frage}</div>
              <div style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {a.antwort?.slice(0, 500)}{a.antwort?.length > 500 ? '…' : ''}
              </div>
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 8 }}>
                {a.status} · {a.beantwortet_am ? new Date(a.beantwortet_am).toLocaleString('de-DE') : a.erstellt_am}
                {a.verarbeitungsdauer_ms ? ` · ${a.verarbeitungsdauer_ms}ms` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab: Wissensuche */}
      {tab === 'suche' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <input
              value={suchQuery}
              onChange={e => setSuchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSuche()}
              placeholder="Wissen durchsuchen…"
              style={{
                flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: 6,
                color: '#f1f5f9', padding: '10px 14px', fontSize: 14,
              }}
            />
            <button onClick={handleSuche} className="btn-primary">🔎 Suchen</button>
          </div>

          {suchErgebnisse && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 15, marginBottom: 10 }}>Wissensbasis ({suchErgebnisse.wissen.length})</h3>
                {suchErgebnisse.wissen.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: 13 }}>Keine Einträge gefunden.</div>
                ) : suchErgebnisse.wissen.map(w => (
                  <div key={w.id} style={{ ...card, padding: 12 }}>
                    <div style={{ fontSize: 12, color: '#6366f1', marginBottom: 4 }}>{TYPEN_LABEL[w.wissenstyp] || w.wissenstyp}</div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{w.titel || '—'}</div>
                    <div style={{ color: '#94a3b8', fontSize: 13 }}>{w.inhalt?.slice(0, 200)}…</div>
                  </div>
                ))}
              </div>

              <div>
                <h3 style={{ fontSize: 15, marginBottom: 10 }}>Klauseln ({suchErgebnisse.klauseln.length})</h3>
                {suchErgebnisse.klauseln.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: 13 }}>Keine Klauseln gefunden.</div>
                ) : suchErgebnisse.klauseln.map(k => (
                  <div key={k.id} style={{ ...card, padding: 12, borderLeft: `3px solid ${RISIKO_COLOR[k.risiko_stufe] || '#6366f1'}` }}>
                    <div style={{ fontSize: 12, color: RISIKO_COLOR[k.risiko_stufe] || '#94a3b8', marginBottom: 4 }}>
                      {k.klauseltyp} · Risiko: {k.risiko_stufe}
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{k.titel}</div>
                    <div style={{ color: '#94a3b8', fontSize: 13 }}>{k.inhalt?.slice(0, 200)}…</div>
                    {k.originaldateiname && <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>📄 {k.originaldateiname}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
